import { Point } from 'geojson';
import { ToastrService } from 'ngx-toastr';
import pluralize from 'pluralize';
import { Observable, of, Subject, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import {
  ISnMapResponse,
  ISnMapQueryParams,
  ISnListResponse,
  ISnListQueryParameters,
  ISnFeedQueryParameters,
  transformListResponse,
} from '@lib/api';
import {
  SnCamera,
  SnShare,
  SnCameraType,
  SnCameraUrls,
  SnLiveApplication,
} from '@lib/api/entities';
import { ISnCanShare } from '@lib/share';
import { t } from '@lib/translation';
import { SnDialogService } from '@lib/ui/dialog';

@Injectable({ providedIn: 'root' })
export class SnCamerasAPI implements ISnCanShare {
  private _targetCamera$ = new Subject<SnCamera>();
  private _hotPursuit$ = new Subject<SnCamera>();
  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _toastrService: ToastrService,
    private readonly _dialogService: SnDialogService,
  ) {}

  public setTargetCamera(camera: SnCamera): void {
    this._targetCamera$.next(camera);
  }

  public setHotPursuit(camera?: SnCamera): void {
    this._hotPursuit$.next(camera);
  }

  public getHotPursuit(): Observable<SnCamera> {
    return this._hotPursuit$.asObservable();
  }

  public getTargetCamera(): Observable<SnCamera> {
    return this._targetCamera$.asObservable();
  }

  public getLiveApplicationList(
    applianceId: string,
    params?: ISnListQueryParameters,
  ): Observable<ISnListResponse<SnLiveApplication>> {
    return this._httpClient
      .get(`/api/camera-appliances/${applianceId}/live-apps/`, { params })
      .pipe(
        map((response: any) => ({
          count: response.count,
          totalPages: response.totalPages,
          results: response.results.map((liveApp) => new SnLiveApplication(liveApp)),
        })),
      );
  }

  public getCameraList(params?: ISnListQueryParameters): Observable<ISnListResponse<SnCamera>> {
    return this._httpClient.get('/api/cameras/', { params }).pipe(
      map((response: any) => transformListResponse(response, (item) => new SnCamera(item))),
      tap({
        error: () => this._toastrService.error(t('cameras.errors.couldNotRetrieveCameras')),
      }),
    );
  }

  public getCameraUrls(params?: ISnListQueryParameters): Observable<ISnListResponse<SnCamera>> {
    return this._httpClient.get('/api/cameras/urls/', { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.map((url) => new SnCameraUrls(url)),
      })),
      tap({
        error: () => this._toastrService.error(t('cameras.errors.couldNotRetrievePlaybackUrls')),
      }),
    );
  }

  public getNearCamera(
    lon: number,
    lat: number,
    radius: number,
    cameras?: number,
  ): Observable<SnCamera[]> {
    if (cameras === 0) {
      return of([]);
    }
    return this._httpClient
      .get(`/api/cameras/?distance_point=${lon},${lat}`, {
        params: {
          ordering: 'distance',
          pageSize: cameras ? cameras.toString() : 'all',
          page: '1',
          status__in: 'Unmanaged,Up',
        },
      })
      .pipe(
        map((response: any) => {
          return response.results
            .filter((r) => r.distance <= radius && r.geometry)
            .map((camera: any) => new SnCamera(camera));
        }),
      );
  }

  public getNearbyCamerasByDistance(
    lon: number,
    lat: number,
    radiusInMeters: number,
    cameras: number,
    camerasParam: string = '',
    deselectedCameraGroupsParam: string = '',
  ): Observable<SnCamera[]> {
    if (cameras === 0) {
      return of([]);
    }

    return this._httpClient
      .get(`/api/cameras/?distance_point=${lon},${lat}`, {
        params: {
          point: `${lon},${lat}`,
          dist: radiusInMeters,
          ordering: 'distance',
          pageSize: cameras.toString(),
          page: '1',
          status__in: camerasParam,
          camera_group__not_in: deselectedCameraGroupsParam,
        },
      })
      .pipe(
        map((response: any) => {
          return response.results.map((camera: any) => new SnCamera(camera));
        }),
        catchError((error) => {
          this._toastrService.error(t('cameras.errors.couldNotRetrieveCameras'));
          return of([]);
        }),
      );
  }

  public getRadarCamera(
    lon: number,
    lat: number,
    radius: number,
    result: number,
    id: string,
    disposition?: string,
  ): Observable<SnCamera[]> {
    return this._httpClient
      .get(`/api/cameras/?distance_point=${lon},${lat}`, {
        params: {
          ordering: 'distance',
          isLive: 'true',
          pageSize: `${result}`,
          page: '1',
          status__in: 'Up',
          disposition,
        },
      })
      .pipe(
        map((response: any) => {
          return response.results
            .filter((r) => r.distance <= radius && r.id !== id)
            .map((camera: any) => new SnCamera(camera));
        }),
      );
  }

  public feedCameras(params: ISnFeedQueryParameters): Observable<SnCamera[]> {
    return this._httpClient
      .get('/api/cameras/feed/', { params })
      .pipe(map((response: any) => response.map((responseItem) => new SnCamera(responseItem))));
  }

  public getCamera(id: string): Observable<SnCamera> {
    return this._httpClient
      .get(`/api/cameras/${id}/`)
      .pipe(map((response: any) => new SnCamera(response)));
  }

  public getCameraStatusHistory(
    cameraId: string,
    params?: ISnListQueryParameters,
  ): Observable<any> {
    return this._httpClient
      .get(`/api/cameras/status-historical/${cameraId}/`, { params })
      .pipe
      // map((response: any) => new SnCameraStatusHistoricalList(response)),
      ();
  }

  public getCameraSettings(id: string): any {
    return this._httpClient.get(`/api/cameras/${id}/settings/`);
  }

  public createCamera(data: any): Observable<SnCamera> {
    return this._httpClient.post('/api/cameras/', data).pipe(
      map((response: any) => new SnCamera(response)),
      tap(() => this._toastrService.success(t('cameras.messages.cameraCreated'))),
    );
  }

  public updateCamera(id: string, data: any): any {
    return this._httpClient.patch(`/api/cameras/${id}/`, data).pipe(
      map((response: any) => new SnCamera(response)),
      tap({
        next: () => this._toastrService.success(t('cameras.messages.cameraUpdated')),
        error: () => this._toastrService.error(t('cameras.errors.unableToUpdateCamera')),
      }),
    );
  }

  public starredCamera(id: string): Observable<any> {
    return this._httpClient
      .post(`/api/cameras/${id}/star/`, {})
      .pipe(tap(() => this._toastrService.success(t('cameras.messages.cameraFavorited'))));
  }

  public notStarredCamera(id: string): Observable<any> {
    return this._httpClient
      .post(`/api/cameras/${id}/unstar/`, {})
      .pipe(tap(() => this._toastrService.success(t('cameras.messages.cameraUnfavorited'))));
  }

  public updateCameraSettings(id: string, data: any): any {
    return this._httpClient
      .patch(`/api/cameras/${id}/settings/`, data)
      .pipe(tap(() => this._toastrService.success(t('cameras.messages.cameraSettingsUpdated'))));
  }

  public deleteCamera(id: string): Observable<Object> {
    return this._dialogService
      .confirm({
        message: t('cameras.confirmations.deleteCamera'),
        confirmButton: {
          text: t('common.delete'),
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => this._httpClient.delete(`/api/cameras/${id}/`)),
        tap({
          next: () => this._toastrService.success(t('cameras.messages.cameraDeleted')),
          error: () => this._toastrService.error(t('cameras.errors.couldNotDeleteCamera')),
        }),
      );
  }

  public importCameras(file: File): Observable<{ created: any; updated: any }> {
    const formData = new FormData();
    formData.append('file', file);

    return this._httpClient.post('/api/cameras/import/from-csv/', formData).pipe(
      tap({
        error: () => this._toastrService.error(t('cameras.errors.couldNotImportCameras')),
      }),
      switchMap((response: any) => {
        const { created, updated, errors } = response;

        if (created) {
          this._toastrService.success(`${created} ${pluralize('camera', created)} created`);
        }

        if (updated) {
          this._toastrService.warning(`${updated} ${pluralize('camera', updated)} updated`);
        }

        if (errors) {
          return throwError(errors);
        }

        return of({ created, updated });
      }),
    );
  }

  public getShareList(
    cameraId: string,
    params?: ISnListQueryParameters,
  ): Observable<ISnListResponse<SnShare>> {
    return this._httpClient.get(`/api/cameras/${cameraId}/shares/`, { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.map((share) => new SnShare(share)),
      })),
    );
  }

  public createShare(cameraId: string, data: any): Observable<SnShare | SnShare[]> {
    return this._httpClient.post(`/api/cameras/${cameraId}/shares/`, data).pipe(
      map((response: any) => {
        return Array.isArray(response)
          ? response.map((share) => new SnShare(share))
          : new SnShare(response);
      }),
    );
  }

  public updateShare(cameraId: string, shareId: string, data: any): Observable<SnShare> {
    return this._httpClient
      .patch(`/api/cameras/${cameraId}/shares/${shareId}/`, data)
      .pipe(map((response: any) => new SnShare(response)));
  }

  public deleteShare(cameraId: string, shareId: string): Observable<Object> {
    return this._dialogService
      .confirm({
        message: t('cameras.confirmations.deleteShare'),
        confirmButton: {
          text: t('common.delete'),
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => {
          return this._httpClient.delete(`/api/cameras/${cameraId}/shares/${shareId}/`);
        }),
      );
  }

  public deleteRoi(camera: SnCamera): Observable<Object> {
    return this._dialogService
      .confirm({
        message: t('cameras.confirmations.deleteRegionOfInterest'),
        confirmButton: {
          text: t('common.delete'),
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => {
          return this._httpClient.patch(`/api/cameras/${camera.id}/`, {
            aiDetectionTypes: [
              {
                id: camera.aiDetectionTypes[0].id,
                roi: [],
                schedules: camera.aiDetectionTypes[0].schedules,
                detectionTimeout: camera.aiDetectionTypes[0].detectionTimeout,
              },
            ],
          });
        }),
        tap({
          next: () => this._toastrService.success(t('cameras.messages.regionDeleted')),
          error: () => this._toastrService.error(t('cameras.errors.couldNotDeleteRegion')),
        }),
      );
  }

  public getCameraMap(
    params: ISnListQueryParameters | ISnMapQueryParams,
  ): Observable<ISnMapResponse<SnCamera>> {
    return this._httpClient.get('/api/maps/static/cameras/', { params }).pipe(
      catchError(() => of<ISnMapResponse<SnCamera>>({ results: [], clusterResults: [] })),
      map((response: any) => {
        return {
          results: response.results.map((camera) => new SnCamera(camera)),
          clusterResults: response.clusterResults || [],
        };
      }),
    );
  }

  public getCameraMapBrief(
    params: ISnListQueryParameters | ISnMapQueryParams,
  ): Observable<ISnMapResponse<SnCamera>> {
    return this._httpClient.get('/api/maps/static/cameras/brief/', { params }).pipe(
      map((response: any) => {
        return {
          results: response.map((camera) => new SnCamera(camera)),
          clusterResults: response.clusterResults || [],
        };
      }),
    );
  }

  public getCameraTypeList(
    params?: ISnListQueryParameters,
  ): Observable<ISnListResponse<SnCameraType>> {
    return this._httpClient.get('/api/cameras/types/', { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.map((location) => new SnCameraType(location)),
      })),
    );
  }

  public getAiCamera(params?: any): Observable<any> {
    return this._httpClient.get('/api/ai/', { params });
  }

  public flagAiDetection(id: string, detectionId: string, query: any): Observable<any> {
    return this._httpClient.put(`/api/ai/${id}/flag_detection/${detectionId}/`, { query });
  }

  public getAiList(params?: ISnListQueryParameters): Observable<any> {
    params = {
      appliance_serial_numbers__size: 1,
      cameras__size: 1,
      ...params,
    };

    if (Array.isArray(params.camera_id__in)) {
      params.camera_id__in = params.camera_id__in.join(',');
    }

    if (Array.isArray(params.label__in)) {
      params.label__in = params.label__in.join(',');
    }

    return this._httpClient.post('/api/ai/', params);
  }

  public getGeoCount(
    points: string,
    distance?: number,
    params?: ISnListQueryParameters,
  ): Observable<ISnListResponse<{ geometry: Point; count: number }>> {
    params = {
      points,
      distance: distance || 0.1,
      ...params,
    };
    return this._httpClient.get('/api/cameras/geo_count/', { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results,
      })),
    );
  }

  public getDescendantList(
    parentCameraId: string,
    params?: ISnListQueryParameters,
  ): Observable<ISnListResponse<SnCamera>> {
    return this._httpClient.get(`/api/cameras/${parentCameraId}/descendant/`, { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.map((camera) => new SnCamera(camera)),
      })),
      tap({
        error: () => this._toastrService.error(t('cameras.errors.couldNotRetrieveCameras')),
      }),
    );
  }

  public getDescendant(parentCameraId: string, descendantId: string): Observable<SnCamera> {
    return this._httpClient
      .get(`/api/cameras/${parentCameraId}/descendant/${descendantId}/`)
      .pipe(map((response: any) => new SnCamera(response)));
  }

  public updateDescendant(
    parentCamera: string,
    descendant: string,
    data: any,
  ): Observable<SnCamera> {
    return this._httpClient
      .patch(`/api/cameras/${parentCamera}/descendant/${descendant}/`, data)
      .pipe(
        map((response: any) => new SnCamera(response)),
        tap(() => this._toastrService.success(t('cameras.messages.cameraUpdated'))),
      );
  }

  public deleteDescendant(parentCameraId: string, descendantId: string): Observable<Object> {
    return this._dialogService
      .confirm({
        message: t('cameras.confirmations.deleteCamera'),
        confirmButton: {
          text: t('common.delete'),
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() =>
          this._httpClient.delete(`/api/cameras/${parentCameraId}/descendant/${descendantId}/`),
        ),
        tap({
          next: () => this._toastrService.success(t('cameras.messages.cameraDeleted')),
          error: () => this._toastrService.error(t('cameras.errors.couldNotDeleteCamera')),
        }),
      );
  }

  public removeAiDetection(id: string, aiDetectionTypes: any): any {
    return this._httpClient
      .patch(`/api/cameras/${id}/`, { aiDetectionTypes })
      .pipe(tap(() => this._toastrService.success(t('cameras.messages.detectionDeleted'))))
      .subscribe();
  }

  public unsetParent(camera: SnCamera): Observable<any> {
    return this._dialogService
      .confirm({
        message: t('cameras.confirmations.removeCameraFromParent'),
        confirmButton: {
          text: t('common.remove'),
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => {
          return this._httpClient.post(
            `/api/cameras/${camera.parentCamera}/descendant/${camera.id}/unset/`,
            {},
          );
        }),
        tap(() => this._toastrService.success(t('cameras.messages.removedFromParent'))),
      );
  }

  public reportIssue(cameraId: string, data: any): Observable<any> {
    return this._httpClient
      .post(`/api/cameras/${cameraId}/support_request/`, { description: data.description })
      .pipe(
        tap({
          next: () => this._toastrService.success(t('cameras.messages.cameraIssueReported')),
          error: () => this._toastrService.error(t('cameras.errors.unableToSubmitCameraIssue')),
        }),
      );
  }

  public getRtspPathRecommendations(params: any): Observable<any> {
    return this._httpClient.get(`/api/cameras/configuration-templates/`, { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results,
      })),
      tap({
        error: () =>
          this._toastrService.error(t('cameras.errors.couldNotRetrieveRtspRecommendations')),
      }),
    );
  }
}
